import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const CONTACT_URL = 'https://readdy.ai/api/form/d6vfqvnb245v7h34onog';

interface FormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  timeline: string;
  message: string;
}

const timelineOptions = [
  { id: 'Standard', days: 30, priceText: '5% of negotiable price', icon: 'ri-calendar-line' },
  { id: 'Express', days: 15, priceText: '7.5% of negotiable price', icon: 'ri-time-line' },
  { id: 'Rush', days: 7, priceText: '10% of negotiable price', icon: 'ri-flashlight-line' },
];

export default function ContactSection() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>({ name: '', email: '', phone: '', service: 'Web Design & Development', timeline: 'Express', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.service || !form.timeline) return;
    if (form.message.length > 500) return;
    setStatus('loading');
    try {
      const selectedTimeline = timelineOptions.find(t => t.id === form.timeline);
      const enhancedMessage = `Service Selected: ${form.service}\nTimeline: ${form.timeline} (${selectedTimeline?.days} Days)\nEstimated Price: ${selectedTimeline?.priceText}\n\nMessage:\n${form.message}`;

      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        message: enhancedMessage,
      });

      // Fire Readdy Forms POST and Supabase lead insert in parallel
      const [res] = await Promise.all([
        fetch(CONTACT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        }),
        supabase.from('leads').insert({
          contact_name: form.name,
          email: form.email,
          phone: form.phone || null,
          service_interest: form.service,
          message: enhancedMessage,
          source: 'website_contact_form',
          status: 'new',
        }).then(({ error }) => {
          if (error) console.warn('[Lead capture] Supabase insert failed:', error.message);
        }),
      ]);

      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) setForm({ name: '', email: '', phone: '', service: 'Web Design & Development', timeline: 'Express', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const contactInfo = [
    { icon: 'ri-map-pin-2-line', label: t('contact.address'), value: 'Kampala, Uganda' },
    { icon: 'ri-phone-line', label: 'Phone', value: t('contact.phoneValue'), href: 'tel:+256773515447' },
    { icon: 'ri-mail-line', label: 'Email', value: t('contact.emailValue'), href: 'mailto:bientomart@gmail.com' },
    { icon: 'ri-time-line', label: 'Hours', value: t('contact.hours') },
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Info */}
          <div>
            <span className="inline-block text-clairBlue-500 text-xs font-bold uppercase tracking-widest mb-4 bg-clairBlue-50 px-4 py-1.5 rounded-full">
              {t('contact.label')}
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-darkBlue-500 mb-5 leading-tight">
              {t('contact.title')}
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-10">{t('contact.subtitle')}</p>

            <div className="space-y-5 mb-10">
              {contactInfo.map((info, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-clairBlue-50 rounded-xl flex-shrink-0">
                    <i className={`${info.icon} text-clairBlue-500`}></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-0.5">{info.label}</div>
                    {info.href ? (
                      <a href={info.href} className="text-darkBlue-500 font-medium text-sm hover:text-clairBlue-500 transition-colors">
                        {info.value}
                      </a>
                    ) : (
                      <div className="text-darkBlue-500 font-medium text-sm">{info.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map Placeholder */}
            <div className="rounded-2xl overflow-hidden h-56 bg-brokenWhite-100 border border-gray-100">
              <iframe
                title="BientoMart Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.7744756319917!2d32.56460247459948!3d0.26860869972874796!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbde1da2ea957%3A0x85289d9db9d330c5!2sASANTE%20MEDIA%20AGENCY!5e0!3m2!1sen!2sug!4v1774373013983!5m2!1sen!2sug"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-4">
                  <i className="ri-check-circle-line text-green-500 text-3xl"></i>
                </div>
                <h3 className="font-display text-2xl font-bold text-darkBlue-500 mb-2">Message Sent!</h3>
                <p className="text-gray-500 mb-6">{t('contact.success')}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="bg-clairBlue-300 text-darkBlue-500 font-semibold px-6 py-2.5 rounded-xl text-sm"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
              >
                <h3 className="font-display text-3xl font-bold text-[#0B1A3A] mb-3">Send us a Message</h3>
                <p className="text-gray-500 text-sm mb-8">Fill in the form below and we&apos;ll respond within 24 hours.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#1A2E4E] mb-2">Full Name *</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <i className="ri-user-line text-gray-400 text-lg"></i>
                      </div>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="w-full bg-white border border-gray-200 rounded-lg pl-11 pr-4 py-3 text-sm text-[#0B1A3A] focus:outline-none focus:border-[#2D73ED] transition-colors"
                        placeholder="Your Name"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#1A2E4E] mb-2">Phone Number *</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <i className="ri-phone-line text-gray-400 text-lg"></i>
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full bg-white border border-gray-200 rounded-lg pl-11 pr-4 py-3 text-sm text-[#0B1A3A] focus:outline-none focus:border-[#2D73ED] transition-colors"
                        placeholder="Your phone number"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-bold text-[#1A2E4E] mb-2">Email Address *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <i className="ri-mail-line text-gray-400 text-lg"></i>
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-lg pl-11 pr-4 py-3 text-sm text-[#0B1A3A] focus:outline-none focus:border-[#2D73ED] transition-colors"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-bold text-[#1A2E4E] mb-2">Service Interested In *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <i className="ri-briefcase-line text-gray-400 text-lg"></i>
                    </div>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-lg pl-11 pr-10 py-3 text-sm text-[#0B1A3A] focus:outline-none focus:border-[#2D73ED] transition-colors appearance-none"
                      required
                    >
                      <option value="">-- Select a Service --</option>
                      <option value="Web Design & Development">Web Design & Development</option>
                      <option value="Social Media Marketing">Social Media Marketing</option>
                      <option value="Traffic Packages">Traffic Packages</option>
                      <option value="AI Tools Consultation">AI Tools Consultation</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                    </div>
                  </div>
                </div>

                <div className="mt-7">
                  <label className="block text-sm font-bold text-[#1A2E4E] mb-1">When do you want this service to be completed? *</label>
                  <p className="text-sm text-gray-500 mb-3">Choose your preferred timeline</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {timelineOptions.map(opt => (
                      <div
                        key={opt.id}
                        onClick={() => setForm(prev => ({ ...prev, timeline: opt.id }))}
                        className={`relative flex flex-col items-center justify-center p-4 rounded-lg border cursor-pointer transition-all ${
                          form.timeline === opt.id
                            ? 'border-[#2D73ED] bg-[#F4F8FF]'
                            : 'border-gray-200 hover:border-blue-200 bg-white'
                        }`}
                      >
                        <div className="absolute top-3 left-3">
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${form.timeline === opt.id ? 'border-[#2D73ED]' : 'border-gray-300'}`}>
                            {form.timeline === opt.id && <div className="w-2 h-2 rounded-full bg-[#2D73ED]"></div>}
                          </div>
                        </div>
                        <i className={`${opt.icon} text-2xl mb-2 ${form.timeline === opt.id ? 'text-[#2D73ED]' : 'text-gray-500'}`}></i>
                        <span className={`text-sm font-bold mb-1 ${form.timeline === opt.id ? 'text-[#0B1A3A]' : 'text-[#0B1A3A]'}`}>{opt.id}</span>
                        <span className="text-xs text-gray-500 mb-1">{opt.days} Days</span>
                        <span className="text-[10px] text-gray-500 text-center leading-tight">{opt.priceText}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 rounded-lg p-5 border border-[#B8D0F8] bg-[#F4F8FF]">
                  <h4 className="text-[#2D73ED] font-bold text-sm mb-4">Your Selection Summary</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#1A2E4E]">Service</span>
                      <span className="text-[#0B1A3A] font-medium text-right">{form.service || 'Not selected'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#1A2E4E]">Timeline</span>
                      <span className="text-[#0B1A3A] font-medium text-right">
                        {form.timeline ? `${form.timeline} (${timelineOptions.find(t => t.id === form.timeline)?.days} Days)` : 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#1A2E4E]">Estimated Completion</span>
                      <span className="text-[#0B1A3A] font-medium text-right">
                        {form.timeline ? `Within ${timelineOptions.find(t => t.id === form.timeline)?.days} days from project start` : 'Not selected'}
                      </span>
                    </div>
                    <div className="border-t border-[#B8D0F8] pt-3 mt-3 flex justify-between items-center">
                      <span className="text-[#0B1A3A] font-bold">Total Price</span>
                      <span className="text-[#2D73ED] font-bold text-sm text-right max-w-[60%]">
                        {form.timeline ? timelineOptions.find(t => t.id === form.timeline)?.priceText : 'Not selected'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex justify-between items-end mb-2">
                    <label className="block text-sm font-bold text-[#1A2E4E]">Your Message * <span className="text-gray-400 font-normal">({form.message.length}/500)</span></label>
                  </div>
                  <div className="relative">
                    <div className="absolute top-3.5 left-0 pl-3.5 flex items-start pointer-events-none">
                      <i className="ri-message-3-line text-gray-400 text-lg"></i>
                    </div>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      className="w-full bg-white border border-gray-200 rounded-lg pl-11 pr-4 py-3 text-sm text-[#0B1A3A] focus:outline-none focus:border-[#2D73ED] transition-colors resize-none"
                      placeholder="Tell us about your project..."
                      required
                    ></textarea>
                  </div>
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-sm mt-4 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {t('contact.error')}
                  </p>
                )}

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={status === 'loading' || form.message.length > 500}
                    className="w-full bg-[#0B1A3A] hover:bg-[#1A2E4E] text-white font-bold py-3.5 rounded-lg transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {status === 'loading' ? (
                      <><i className="ri-loader-4-line animate-spin text-lg"></i> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-line text-lg"></i> Send Message</>
                    )}
                  </button>
                  <div className="mt-4 text-center text-xs text-gray-500 flex items-center justify-center gap-1.5 font-medium">
                    <i className="ri-lock-2-line"></i> Your information is secure and will never be shared.
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
